import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { BrandGuard } from './components/BrandGuard';
import { MainLayout } from './components/layout';
import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/auth/LoginPage';
import SetPasswordPage from './pages/auth/SetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import ForbiddenPage from './pages/ForbiddenPage';
import PlaygroundPage from './pages/PlaygroundPage';
import { PosProductsPage } from './features/pos-products';
import { RawProductsPage } from './features/raw-products';
import { RecipesPage } from './features/recipes';
import { SettingsPage } from './features/settings';
import { PosSalesControlPage } from './features/pos-sales';
import { PurchaseOrdersPage } from './features/purchase-orders';
import { GoodsReceiptsPage } from './features/goods-receipts';
import { SupplierReturnsPage } from './features/supplier-returns';
import { InventoriesPage } from './features/inventories';
import { StockMovementsPage } from './features/stock-movements';
import { CurrentStockPage } from './features/current-stock';
import { LossesPage } from './features/losses';
import { TransfersPage } from './features/transfers';
import { StockReportV2Page } from './features/stock-report';
import { MarginReportPage } from './features/margin-report';
import { PurchaseSummaryPage } from './features/purchase-summary-report';
import { ProductMixPage } from './features/product-mix';
import { AuditLogsPage } from './features/audit-logs';
import { BillAdminPage } from './features/admin';
import './styles/globals.scss';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // Donnees considerees comme perimees immediatement -> refetch a chaque navigation
      staleTime: 0,
      // Refetch quand l'utilisateur revient sur l'onglet du navigateur
      refetchOnWindowFocus: true,
      // Refetch a chaque montage de composant (si stale)
      refetchOnMount: true,
      // Garder les donnees en cache 5 min pour affichage instantane pendant le refetch
      gcTime: 5 * 60 * 1000,
    },
  },
});

// Placeholder pages for protected routes
function ProductsPage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-dark-500">Produits</h1>
      <p className="text-neutral-500 mt-2">Page en cours de developpement...</p>
    </div>
  );
}

// MovementsPage placeholder is no longer needed - using StockMovementsPage

function OrdersPage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-dark-500">Commandes</h1>
      <p className="text-neutral-500 mt-2">Page en cours de developpement...</p>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Routes publiques */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/authenticate/set-password" element={<SetPasswordPage />} />

            {/* Routes protegees avec MainLayout */}
            <Route
              element={
                <ProtectedRoute>
                  <BrandGuard>
                    <MainLayout />
                  </BrandGuard>
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<DashboardPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/pos" element={<PosProductsPage />}>
                <Route path=":productId" element={null} />
                <Route path="create" element={null} />
              </Route>
              <Route path="/products/raw" element={<RawProductsPage />}>
                <Route path=":productId" element={null} />
                <Route path="create" element={null} />
              </Route>
              <Route path="/recettes" element={<RecipesPage />}>
                <Route path=":recipeId" element={null} />
                <Route path="create" element={null} />
              </Route>
              <Route path="/stock/actuel" element={<CurrentStockPage />}>
                <Route path=":rawProductId" element={null} />
              </Route>
              <Route path="/stock/mouvements" element={<StockMovementsPage />} />
              <Route path="/stock/inventaires" element={<InventoriesPage />}>
                <Route path="create" element={null} />
                <Route path=":inventoryId/details" element={null} />
                <Route path=":inventoryId/edit" element={null} />
              </Route>
              <Route path="/stock/pertes" element={<LossesPage />}>
                <Route path=":lossId" element={null} />
              </Route>
              <Route path="/stock/transferts" element={<TransfersPage />}>
                <Route path=":transferId" element={null} />
              </Route>
              <Route path="/rapports/stock" element={<StockReportV2Page />} />
              <Route path="/rapports/marge" element={<MarginReportPage />} />
              <Route path="/rapports/achats" element={<PurchaseSummaryPage />} />
              <Route path="/rapports/product-mix" element={<ProductMixPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/achats/commandes" element={<PurchaseOrdersPage />}>
                <Route path="create" element={null} />
                <Route path=":orderId" element={null} />
                <Route path=":orderId/edit" element={null} />
              </Route>
              <Route path="/achats/receptions" element={<GoodsReceiptsPage />}>
                <Route path="create" element={null} />
                <Route path=":receiptId" element={null} />
              </Route>
              <Route path="/achats/retours-fournisseur" element={<SupplierReturnsPage />}>
                <Route path=":returnId" element={null} />
              </Route>
              <Route path="/controles/ventes-pos" element={<PosSalesControlPage />} />
              <Route path="/controles/audit-logs" element={<AuditLogsPage />} />
              <Route path="/parametres" element={<SettingsPage />}>
                {/* Deep linking for suppliers catalog and price history */}
                <Route path="suppliers/:supplierId/catalog" element={null} />
                <Route path="suppliers/:supplierId/catalog/:catalogItemId/price-history" element={null} />
              </Route>
            </Route>

            {/* SuperAdmin route (hidden, no navigation) */}
            <Route
              path="/bill-admin"
              element={
                <ProtectedRoute requireSuperAdmin>
                  <BillAdminPage />
                </ProtectedRoute>
              }
            />

            {/* Playground (dev only) */}
            {import.meta.env.DEV && (
              <Route path="/playground" element={<PlaygroundPage />} />
            )}

            {/* Pages d'erreur */}
            <Route path="/403" element={<ForbiddenPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgb(var(--color-dark-500))',
            color: 'rgb(var(--color-neutral-50))',
            borderRadius: '0.5rem',
          },
          success: {
            iconTheme: {
              primary: 'rgb(var(--color-success-500))',
              secondary: 'white',
            },
          },
          error: {
            iconTheme: {
              primary: 'rgb(var(--color-danger-500))',
              secondary: 'white',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
